import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PROFILE_TYPE_LABELS, type ProfileType } from "../lib/profiles";

const inputClass = "rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink";
const labelClass = "grid gap-1 text-xs font-bold text-muted";

export function CreateProfile() {
  const { createProfile, profiles } = useProfiles();
  const navigate = useNavigate();

  const [type, setType] = useState<ProfileType>("driver");
  const [displayName, setDisplayName] = useState("");
  const [headline, setHeadline] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [ownerOperator, setOwnerOperator] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (k: string) => (e: { target: { value: string } }) =>
    setFields((f) => ({ ...f, [k]: e.target.value }));
  const list = (v?: string) =>
    (v ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  const num = (v?: string) => (v ? Number(v) : undefined);

  function detailFor(): Record<string, unknown> {
    switch (type) {
      case "driver":
        return {
          cdl_class: fields.cdl_class ?? "A",
          endorsements: list(fields.endorsements),
          years_experience: num(fields.years_experience),
          owner_operator: ownerOperator,
          equipment: list(fields.equipment),
        };
      case "company":
        return {
          legal_name: fields.legal_name || undefined,
          dot_number: fields.dot_number || undefined,
          mc_number: fields.mc_number || undefined,
          fleet_size: num(fields.fleet_size),
          service_areas: list(fields.service_areas),
        };
      case "recruiter":
        return { agency: fields.agency || undefined, specialties: list(fields.specialties) };
      case "broker_dispatcher":
        return { authority_mc: fields.authority_mc || undefined, freight_types: list(fields.freight_types) };
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createProfile({
        type,
        display_name: displayName || undefined,
        headline: headline || undefined,
        detail: detailFor(),
      });
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the profile");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto grid w-full max-w-lg gap-5">
      <div className="grid gap-1">
        <h1 className="font-display text-2xl font-black tracking-tight">
          {profiles.length === 0 ? "Create your first profile" : "New profile"}
        </h1>
        <p className="text-sm text-muted">One account can hold several profiles for different roles.</p>
      </div>

      <form className="grid gap-3" onSubmit={onSubmit}>
        <label className={labelClass}>
          Profile type
          <select
            className={inputClass}
            value={type}
            onChange={(e) => setType(e.target.value as ProfileType)}
          >
            {(Object.keys(PROFILE_TYPE_LABELS) as ProfileType[]).map((t) => (
              <option key={t} value={t}>
                {PROFILE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>

        <label className={labelClass}>
          Display name
          <input className={inputClass} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label className={labelClass}>
          Headline
          <input className={inputClass} value={headline} onChange={(e) => setHeadline(e.target.value)} />
        </label>

        {type === "driver" && (
          <>
            <label className={labelClass}>
              CDL class
              <select className={inputClass} value={fields.cdl_class ?? "A"} onChange={set("cdl_class")}>
                <option>A</option>
                <option>B</option>
                <option>C</option>
              </select>
            </label>
            <label className={labelClass}>
              Endorsements (comma-separated)
              <input className={inputClass} placeholder="H, N" value={fields.endorsements ?? ""} onChange={set("endorsements")} />
            </label>
            <label className={labelClass}>
              Years of experience
              <input className={inputClass} type="number" value={fields.years_experience ?? ""} onChange={set("years_experience")} />
            </label>
            <label className="flex items-center gap-2 text-sm font-bold">
              <input type="checkbox" checked={ownerOperator} onChange={(e) => setOwnerOperator(e.target.checked)} />
              Owner-operator (can also haul loads)
            </label>
            <label className={labelClass}>
              Equipment (comma-separated)
              <input className={inputClass} placeholder="reefer, flatbed" value={fields.equipment ?? ""} onChange={set("equipment")} />
            </label>
          </>
        )}

        {type === "company" && (
          <>
            <label className={labelClass}>
              Legal name
              <input className={inputClass} value={fields.legal_name ?? ""} onChange={set("legal_name")} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className={labelClass}>
                DOT number
                <input className={inputClass} value={fields.dot_number ?? ""} onChange={set("dot_number")} />
              </label>
              <label className={labelClass}>
                MC number
                <input className={inputClass} value={fields.mc_number ?? ""} onChange={set("mc_number")} />
              </label>
            </div>
            <label className={labelClass}>
              Fleet size
              <input className={inputClass} type="number" value={fields.fleet_size ?? ""} onChange={set("fleet_size")} />
            </label>
            <label className={labelClass}>
              Service areas (comma-separated)
              <input className={inputClass} placeholder="TX, Southeast" value={fields.service_areas ?? ""} onChange={set("service_areas")} />
            </label>
          </>
        )}

        {type === "recruiter" && (
          <>
            <label className={labelClass}>
              Agency
              <input className={inputClass} value={fields.agency ?? ""} onChange={set("agency")} />
            </label>
            <label className={labelClass}>
              Specialties (comma-separated)
              <input className={inputClass} placeholder="OTR, regional" value={fields.specialties ?? ""} onChange={set("specialties")} />
            </label>
          </>
        )}

        {type === "broker_dispatcher" && (
          <>
            <label className={labelClass}>
              Broker authority / MC
              <input className={inputClass} value={fields.authority_mc ?? ""} onChange={set("authority_mc")} />
            </label>
            <label className={labelClass}>
              Freight types (comma-separated)
              <input className={inputClass} placeholder="dry van, reefer" value={fields.freight_types ?? ""} onChange={set("freight_types")} />
            </label>
          </>
        )}

        {error && <p className="font-mono text-sm text-danger">{error}</p>}
        <div className="flex gap-3">
          <Button type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create profile"}
          </Button>
          {profiles.length > 0 && (
            <Link to="/" className="self-center text-sm font-bold text-muted">
              Cancel
            </Link>
          )}
        </div>
      </form>
    </div>
  );
}
